/**
 * The run's soft-tissue pass — ADR-038 J2: **the Document's declared regions
 * become leaves on the rig and weights on the flesh, every run.**
 *
 * BOTH STEPS RUN LAST, after every other weight pass (the hand and tail
 * binds, ADR-029's pelvis re-own, the ADR-028 hinge bands and crease morphs):
 *
 *  1. {@link planSoftTissue} — measure the regions on the FINISHED paint (or
 *     take a declared zone's faces), graft one leaf + tip per declared
 *     region, and compute the leaf's influences from the region's feather.
 *     The run then grows the leaves' nodes (`materializeMissingBones`).
 *  2. The seat, through the ONE seat (`bindGraftedDigits`): the leaf takes
 *     its share and every influence already on the vertex keeps its
 *     proportion of the rest. Last on purpose — so no earlier pass reads (or
 *     undoes) a leaf, and nothing outside a region changes (the specimen
 *     probe measures it). The Body card measures the same finished paint, so
 *     what it offers is what the run grafts.
 *
 * WHAT IS EXCLUDED FROM A REGION: flesh a zone calls cloth (a loose garment
 * must not bounce with the breast under it — a skin-tight one is simply
 * flesh to the detector, and a person can declare the zone), and a tail —
 * a tail zone's flesh, or, when the body has no tail zone, the tail the
 * detector finds on the file's paint (its root is the most posterior flesh at
 * glute height; the tiger's was, measured 2026-09-24) — but only one with
 * unarmatured seed flesh: Karo's phantom (2026-09-29) had none.
 *
 * Depends on: kernel (regions, leaves, zones, tail detection), adapter-three
 * (subjectArrays). Depended on by: session/run-document (the replay), the
 * specimen probe.
 */
import {
  detectSoftTissue,
  detectTail,
  detectUnarmatured,
  fingerprintMesh,
  fingerprintMismatch,
  graftSoftTissueLeaves,
  kindOfRegion,
  nativeBustBones,
  softTissueInfluences,
  SoftTissueRefusal,
  SOFT_TISSUE_PEAK_SHARE,
  type AnimatorDocument,
  type EvidenceMarker,
  type JiggleRegionId,
  type RigDescriptor,
  type SoftTissueRegion,
  type SoftTissueRegionId,
  type SoftTissueReport,
  type SoftTissueTier,
  type VertexInfluence,
} from '@harryhausen/kernel';
import { subjectArrays, type SubjectArrays } from '@harryhausen/adapter-three';
import type { Object3D } from 'three';

/** What one declared soft-tissue leaf did, in numbers (bind-world metres). */
export interface SoftTissueGraftRecord {
  /** A body mass, or the hair (ADR-039 T2a — its leaf is grafted by the hair pass, `hair/hair-soften-run`). */
  readonly region: JiggleRegionId;
  readonly source: 'measured' | 'declared';
  readonly zoneId: string | null;
  readonly tier: SoftTissueTier;
  readonly prominence: number;
  /** The leaf and its tip, as rig bones (`LeftBreastJiggle_graft`, `…_End_graft`). */
  readonly bone: string;
  readonly tip: string;
  readonly parent: string;
  readonly head: { readonly x: number; readonly y: number; readonly z: number };
  readonly tail: { readonly x: number; readonly y: number; readonly z: number };
  readonly length: number;
  /** The region's flesh radius — the spring's hit sphere is read from it. */
  readonly radius: number;
  readonly vertices: number;
  /** Vertices the leaf takes a share of (feather > 0). */
  readonly verticesWeighted: number;
  readonly peakShare: number;
  readonly clothFacesSkipped: number;
  readonly reading: string;
}

export interface SoftTissuePlan {
  readonly rig: RigDescriptor;
  readonly records: readonly SoftTissueGraftRecord[];
  /** The measured regions the leaves were grown on (ADR-038 §14: the surface jiggle solves on their patches). */
  readonly regions: readonly SoftTissueRegion[];
  /** One seat per leaf: the leaf + tip names and the leaf's influences. */
  /** Each leaf's seat: its influences are shares of `donor`'s paint (the bone it hangs from) — the rest-follow is unchanged. */
  readonly pending: readonly { readonly region: JiggleRegionId; readonly boneNames: readonly string[]; readonly influences: readonly VertexInfluence[]; readonly donor: string | null }[];
  readonly markers: readonly EvidenceMarker[];
}

export class SoftTissueRunRefusal extends Error {}

/** The rig's mapping as the detector wants it: BBSA id → rig bone. */
export const mappedOf = (rig: RigDescriptor): Record<string, string> => Object.fromEntries(rig.mapping.map((m) => [m.ontologyId, m.rigBone]));

/**
 * Measure every region on a subject, the way the run and the Body card both
 * do: cloth zones out, tails out, declared zones in. `declared` maps a region
 * to the zone a person declared it on.
 */
export function measureSoftTissue(
  doc: AnimatorDocument,
  scene: Object3D,
  rig: RigDescriptor,
  arrays: SubjectArrays = subjectArrays(scene),
): { report: SoftTissueReport; zoneRefusals: readonly string[]; tailExcluded: number } {
  if (arrays.skin === null) throw new SoftTissueRunRefusal(`"${doc.target.label}" is not skinned — soft tissue needs weights to move`);
  const fingerprint = fingerprintMesh(arrays.positions, arrays.indices);
  const zoneRefusals: string[] = [];
  const zonesOk = (doc.zones ?? []).filter((z) => {
    const mismatch = fingerprintMismatch(z.meshFingerprint, fingerprint);
    if (mismatch !== null && (z.intent === 'cloth' || z.intent === 'softTissue' || z.intent === 'tail')) zoneRefusals.push(`zone "${z.name}" was ${mismatch}`);
    return mismatch === null;
  });
  const clothFaces = zonesOk.filter((z) => z.intent === 'cloth').flatMap((z) => z.faces);
  // A tail's flesh: the tail zones' vertices, else the detector's tail on the file's paint.
  const I = arrays.indices;
  const tailVertices = new Set<number>();
  for (const z of zonesOk.filter((zz) => zz.intent === 'tail')) for (const f of z.faces) for (let c = 0; c < 3; c++) tailVertices.add(I[f * 3 + c]!);
  if (tailVertices.size === 0) {
    // Only a detected tail with flesh NO bone reaches is carved out. A body without a
    // tail can still read as one: Karo's (2026-09-29) was his gluteal cleft running up
    // his lower back — 16 cm, every seed pelvis-owned, none unarmatured — and carving it
    // took the heart of his right buttock, so that glute's apex fell to the thigh and its
    // leaf swung the thigh. Real unrigged tails carry unarmatured seeds (tiger 266,
    // ActorCore kobold 1190); a person's declared tail zone is always honoured above.
    const tail = detectTail(subjectArrays(scene, { paint: 'file' })).tail;
    if (tail !== null && tail.seeds.unarmatured > 0) for (const v of tail.vertexIndices) tailVertices.add(v);
  }
  // Loose flesh — what the zone proposer reads as a hanging sheet or tube (a coat
  // tail, a skirt flap), at main.ts's own defaults — is never a glute.
  const loose = new Set<number>();
  for (const c of detectUnarmatured(arrays, { thresholdMultiplier: 2, growRings: 2 }).clusters) {
    if (c.kind === 'sheet' || c.kind === 'tube') for (const v of c.vertexIndices) loose.add(v);
  }
  const declared: Partial<Record<SoftTissueRegionId, { zoneName: string; faces: readonly number[] }>> = {};
  for (const s of doc.target.softTissue ?? []) {
    if (s.zoneId === undefined) continue;
    const zone = zonesOk.find((z) => z.id === s.zoneId);
    if (zone === undefined) continue; // refused above (fingerprint) — the run refuses by name
    declared[s.region] = { zoneName: zone.name, faces: zone.faces };
  }
  const report = detectSoftTissue({
    positions: arrays.positions,
    indices: arrays.indices,
    bones: arrays.bones,
    skin: arrays.skin,
    mapped: mappedOf(rig),
    up: rig.upAxis === 'Z' ? { x: 0, y: 0, z: 1 } : { x: 0, y: 1, z: 0 },
    excludeFaces: clothFaces,
    excludeVertices: [...tailVertices],
    looseVertices: [...loose],
    declared,
  });
  return { report, zoneRefusals, tailExcluded: tailVertices.size };
}

/**
 * The bone that owns each region's flesh: the feather-weighted majority of its
 * paint (the feather is the leaf's share, so the bone that matters most is the
 * one the leaf replaces at the centre). The leaf hangs from it — see
 * `graftSoftTissueLeaves`' `owners`.
 */
export function regionOwners(arrays: SubjectArrays, regions: readonly SoftTissueRegion[]): Map<SoftTissueRegionId, string> {
  const out = new Map<SoftTissueRegionId, string>();
  const skin = arrays.skin;
  if (skin === null) return out;
  for (const r of regions) {
    const sum = new Map<string, number>();
    r.vertices.forEach((v, i) => {
      const f = r.feather[i] ?? 0;
      for (let k = 0; k < 4; k++) {
        const w = skin.weight[v * 4 + k]!;
        if (w <= 0) continue;
        const name = arrays.bones[skin.index[v * 4 + k]!]!.name;
        sum.set(name, (sum.get(name) ?? 0) + f * w);
      }
    });
    let best: string | undefined;
    for (const [name, w] of sum) if (best === undefined || w > sum.get(best)!) best = name;
    if (best !== undefined) out.set(r.id, best);
  }
  return out;
}

/** Step 1: measure, graft, weigh — the rig gains the leaves; the seat comes later. */
export function planSoftTissue(doc: AnimatorDocument, scene: Object3D, rig: RigDescriptor): SoftTissuePlan {
  const decls = doc.target.softTissue ?? [];
  if (decls.length === 0) return { rig, records: [], regions: [], pending: [], markers: [] };
  // A body that wears its own bust bones (VRoid) keeps them — refused BEFORE
  // anything is measured, so the sentence names the bones, not the skins.
  const bust = nativeBustBones(rig);
  for (const s of decls) {
    const native = s.region === 'LeftBreast' ? bust.left : s.region === 'RightBreast' ? bust.right : [];
    if (native.length > 0) {
      throw new SoftTissueRunRefusal(`${s.region} leaf refused: this body carries its own bust bones (${native.join(', ')}) — they are its breast spring; a leaf would be a second mass in the same flesh`);
    }
  }
  const { report, zoneRefusals, tailExcluded } = measureSoftTissue(doc, scene, rig);
  for (const s of decls) {
    if (s.zoneId === undefined) continue;
    const zone = (doc.zones ?? []).find((z) => z.id === s.zoneId);
    const refusal = zoneRefusals.find((r) => zone !== undefined && r.startsWith(`zone "${zone.name}"`));
    if (refusal !== undefined) throw new SoftTissueRunRefusal(`${s.region} leaf refused: ${refusal} — declare it again on this character (ADR-031 D2).`);
  }
  if (report.frame === null) throw new SoftTissueRunRefusal(`soft tissue refused: ${report.frameRefusal ?? 'no body frame'}`);
  const regions: SoftTissueRegion[] = [];
  for (const s of decls) {
    const r = report.regions.find((x) => x.id === s.region);
    if (r === undefined) {
      const why = report.unmeasured.find((u) => u.id === s.region)?.why ?? 'not measured';
      throw new SoftTissueRunRefusal(`${s.region} leaf refused: ${why}`);
    }
    regions.push(r);
  }
  let grafted;
  try {
    grafted = graftSoftTissueLeaves(rig, regions, regionOwners(subjectArrays(scene), regions));
  } catch (err) {
    if (err instanceof SoftTissueRefusal) throw new SoftTissueRunRefusal(`soft tissue refused — ${err.message}`);
    throw err;
  }
  const records: SoftTissueGraftRecord[] = [];
  const pending: { region: JiggleRegionId; boneNames: string[]; influences: VertexInfluence[]; donor: string | null }[] = [];
  const markers: EvidenceMarker[] = [];
  for (const leaf of grafted.leaves) {
    const r = regions.find((x) => x.id === leaf.region)!;
    const influences = softTissueInfluences(r, leaf.bone);
    const decl = decls.find((d) => d.region === leaf.region)!;
    // A breast / belly leaf hangs from its flesh's measured owner and takes only
    // that owner's weight (rest-exact). A glute hangs from the pelvis (ADR-029)
    // while its flesh is largely the thigh's: as a donor the pelvis would starve
    // it, so a glute keeps the uniform seat (donor null).
    pending.push({ region: leaf.region, boneNames: [leaf.bone, leaf.tip], influences, donor: kindOfRegion(leaf.region) === 'glute' ? null : leaf.parent });
    records.push({
      region: leaf.region,
      source: r.source,
      zoneId: decl.zoneId ?? null,
      tier: r.tier,
      prominence: r.prominence,
      bone: leaf.bone,
      tip: leaf.tip,
      parent: leaf.parent,
      head: leaf.head,
      tail: leaf.tail,
      length: leaf.length,
      radius: r.radius,
      vertices: r.vertices.length,
      verticesWeighted: influences.length,
      peakShare: SOFT_TISSUE_PEAK_SHARE,
      clothFacesSkipped: r.clothFacesSkipped,
      reading: r.reading,
    });
    markers.push({
      code: 'soft-tissue-leaf-grafted',
      detail: {
        region: leaf.region,
        source: r.source,
        tier: r.tier,
        prominenceCm: Number((r.prominence * 100).toFixed(2)),
        leafCm: Number((leaf.length * 100).toFixed(2)),
        vertices: r.vertices.length,
        weighted: influences.length,
        clothFacesSkipped: r.clothFacesSkipped,
        tailVerticesExcluded: tailExcluded,
      },
    });
  }
  return { rig: grafted.rig, records, regions, pending, markers };
}
